/**
 * Primitive Design Tokens
 * Raw values that form the foundation of the design system
 * These should never be used directly in components - use semantic tokens instead
 */

// Color Palette — Caribbean Blue rebrand
export const primitiveColors = {
  // Ink neutral scale — warm-cool balanced slate
  neutral: {
    0:   "#FFFFFF",
    50:  "#F7F8FA",
    100: "#EEF0F4",
    200: "#DDE1E8",
    300: "#BCC2CE",
    400: "#8E96A6",
    500: "#6B7280",
    600: "#535A68",
    700: "#3D434F",
    800: "#2B2F39",
    900: "#23262F",
    950: "#1C1F26",
    1000: "#14161C",
  },

  // Caribbean Blue — primary brand palette
  blue: {
    50:  "#ECFBFE",
    100: "#CEF3FB",
    200: "#A4E8F6",
    300: "#6DD7EE",
    400: "#38C2E0",
    500: "#1CA9CC",
    600: "#1389AB",
    700: "#146E8A",
    800: "#155870",
    900: "#134759",
    950: "#0A2E3B",
  },

  // Accent colors
  green: {
    50: "#f0fdf4",
    100: "#dcfce7",
    200: "#bbf7d0",
    300: "#86efac",
    400: "#4ade80",
    500: "#22c55e",
    600: "#16a34a",
    700: "#15803d",
    800: "#166534",
    900: "#14532d",
    950: "#052e16",
  },

  red: {
    50: "#fef2f2",
    100: "#fee2e2",
    200: "#fecaca",
    300: "#fca5a5",
    400: "#f87171",
    500: "#ef4444",
    600: "#dc2626",
    700: "#b91c1c",
    800: "#991b1b",
    900: "#7f1d1d",
    950: "#450a0a",
  },

  amber: {
    50: "#fffbeb",
    100: "#fef3c7",
    200: "#fde68a",
    300: "#fcd34d",
    400: "#fbbf24",
    500: "#f59e0b",
    600: "#d97706",
    700: "#b45309",
    800: "#92400e",
    900: "#78350f",
    950: "#451a03",
  },

  purple: {
    50: "#faf5ff",
    100: "#f3e8ff",
    200: "#e9d5ff",
    300: "#d8b4fe",
    400: "#c084fc",
    500: "#a855f7",
    600: "#9333ea",
    700: "#7c3aed",
    800: "#6b21a8",
    900: "#581c87",
    950: "#3b0764",
  },

  // Pure colors
  pure: {
    white: "#ffffff",
    black: "#000000",
    transparent: "transparent",
  },
} as const;

// Typography Scale - Fluid and responsive
export const primitiveTypography = {
  // Font families
  fontFamily: {
    sans: [
      "Inter Tight",
      "ui-sans-serif",
      "system-ui",
      "-apple-system",
      "BlinkMacSystemFont",
      "Segoe UI",
      "Roboto",
      "Helvetica Neue",
      "Arial",
      "Noto Sans",
      "sans-serif",
    ],
    mono: [
      "JetBrains Mono",
      "ui-monospace",
      "SFMono-Regular",
      "Menlo",
      "Monaco",
      "Consolas",
      "Liberation Mono",
      "Courier New",
      "monospace",
    ],
    serif: [
      "ui-serif",
      "Georgia",
      "Cambria",
      "Times New Roman",
      "Times",
      "serif",
    ],
  },

  // Font weights
  fontWeight: {
    thin: 100,
    extralight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },

  // Font sizes - Base values for fluid scaling
  fontSize: {
    xs: {
      size: "0.75rem",
      lineHeight: "1rem",
    },
    sm: {
      size: "0.875rem",
      lineHeight: "1.25rem",
    },
    base: {
      size: "1rem",
      lineHeight: "1.5rem",
    },
    lg: {
      size: "1.125rem",
      lineHeight: "1.75rem",
    },
    xl: {
      size: "1.25rem",
      lineHeight: "1.75rem",
    },
    "2xl": {
      size: "1.5rem",
      lineHeight: "2rem",
    },
    "3xl": {
      size: "1.875rem",
      lineHeight: "2.25rem",
    },
    "4xl": {
      size: "2.25rem",
      lineHeight: "2.5rem",
    },
    "5xl": {
      size: "3rem",
      lineHeight: "1.2",
    },
    "6xl": {
      size: "3.75rem",
      lineHeight: "1.1",
    },
    "7xl": {
      size: "4.5rem",
      lineHeight: "1.1",
    },
    "8xl": {
      size: "6rem",
      lineHeight: "1",
    },
    "9xl": {
      size: "8rem",
      lineHeight: "1",
    },
  },

  // Letter spacing
  letterSpacing: {
    tighter: "-0.05em",
    tight: "-0.025em",
    normal: "0em",
    wide: "0.025em",
    wider: "0.05em",
    widest: "0.1em",
  },

  // Line heights
  lineHeight: {
    none: "1",
    tight: "1.25",
    snug: "1.375",
    normal: "1.5",
    relaxed: "1.625",
    loose: "2",
  },
} as const;

// Spacing Scale - Systematic and consistent
export const primitiveSpacing = {
  0: "0px",
  px: "1px",
  0.5: "0.125rem", // 2px
  1: "0.25rem", // 4px
  1.5: "0.375rem", // 6px
  2: "0.5rem", // 8px
  2.5: "0.625rem", // 10px
  3: "0.75rem", // 12px
  3.5: "0.875rem", // 14px
  4: "1rem", // 16px
  5: "1.25rem", // 20px
  6: "1.5rem", // 24px
  7: "1.75rem", // 28px
  8: "2rem", // 32px
  9: "2.25rem", // 36px
  10: "2.5rem", // 40px
  11: "2.75rem", // 44px
  12: "3rem", // 48px
  14: "3.5rem", // 56px
  16: "4rem", // 64px
  20: "5rem", // 80px
  24: "6rem", // 96px
  28: "7rem", // 112px
  32: "8rem", // 128px
  36: "9rem", // 144px
  40: "10rem", // 160px
  44: "11rem", // 176px
  48: "12rem", // 192px
  52: "13rem", // 208px
  56: "14rem", // 224px
  60: "15rem", // 240px
  64: "16rem", // 256px
  72: "18rem", // 288px
  80: "20rem", // 320px
  96: "24rem", // 384px
} as const;

// Border Radius - Systematic scaling
export const primitiveBorderRadius = {
  none: "0px",
  xs: "0.125rem", // 2px
  sm: "0.25rem", // 4px
  base: "0.375rem", // 6px
  md: "0.5rem", // 8px
  lg: "0.75rem", // 12px
  xl: "1rem", // 16px
  "2xl": "1.5rem", // 24px
  "3xl": "2rem", // 32px
  full: "9999px",
} as const;

// Shadows - Layered shadow system
export const primitiveShadows = {
  xs: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  sm: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  base: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  md: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  lg: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
  xl: "0 25px 50px -12px rgb(0 0 0 / 0.25)",
  "2xl": "0 50px 100px -20px rgb(0 0 0 / 0.25)",
  inner: "inset 0 2px 4px 0 rgb(0 0 0 / 0.05)",
  none: "0 0 #0000",
} as const;

// Z-Index Scale - Systematic layering
export const primitiveZIndex = {
  hide: -1,
  auto: "auto",
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
  max: 2147483647,
} as const;

// Breakpoints - Mobile-first responsive design
export const primitiveBreakpoints = {
  xs: "475px",
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
  "3xl": "1600px",
} as const;

// Animation durations and easings
export const primitiveAnimations = {
  duration: {
    instant: "0ms",
    fast: "120ms",
    normal: "200ms",
    slow: "360ms",
    slower: "500ms",
    slowest: "1000ms",
  },

  easing: {
    linear: "linear",
    in: "cubic-bezier(0.4, 0, 1, 1)",
    out: "cubic-bezier(0.2, 0.8, 0.2, 1)",
    inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    bounce: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
    elastic: "cubic-bezier(0.175, 0.885, 0.32, 1.275)",
  },
} as const;

// Export all primitive tokens
export const primitiveTokens = {
  colors: primitiveColors,
  typography: primitiveTypography,
  spacing: primitiveSpacing,
  borderRadius: primitiveBorderRadius,
  shadows: primitiveShadows,
  zIndex: primitiveZIndex,
  breakpoints: primitiveBreakpoints,
  animations: primitiveAnimations,
} as const;

// Type exports for TypeScript intellisense
export type PrimitiveColors = typeof primitiveColors;
export type PrimitiveTypography = typeof primitiveTypography;
export type PrimitiveSpacing = typeof primitiveSpacing;
export type PrimitiveBorderRadius = typeof primitiveBorderRadius;
export type PrimitiveShadows = typeof primitiveShadows;
export type PrimitiveZIndex = typeof primitiveZIndex;
export type PrimitiveBreakpoints = typeof primitiveBreakpoints;
export type PrimitiveAnimations = typeof primitiveAnimations;
export type PrimitiveTokens = typeof primitiveTokens;
